import { useEffect, useRef, useState } from 'react';
import { createBlock } from '../../../utils/blockUtils';
import { blocksToPlainText } from '../../../utils/blockClipboard';
import {
  flattenCalloutLayout,
  getCalloutColumnCount,
  readCalloutColumnsProp,
  resolveCalloutColumnCount,
  setCalloutColumnCount,
} from '../../../utils/calloutColumns';

/**
 * Sticky column packing, the 1–6 picker, and Copy-as-plain-text.
 */
export const useCalloutColumns = ({ block, onUpdate, resolveVariable }) => {
  const [copied, setCopied] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnsRef = useRef(null);

  // Columns mode is sticky: if the body isn't a matching column row yet
  // (loaded flat, or just turned on), pack it so the layout stays even.
  useEffect(() => {
    const n = readCalloutColumnsProp(block.props);
    if (n < 2) return;
    if (getCalloutColumnCount(block.children) === n) return;
    onUpdate(block.id, { children: setCalloutColumnCount(block.children, n) });
  }, [block.id, block.props, block.children, onUpdate]);

  useEffect(() => {
    if (!columnsOpen) return undefined;
    const onDown = (e) => {
      if (columnsRef.current?.contains(e.target)) return;
      setColumnsOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setColumnsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [columnsOpen]);

  const columnCount = resolveCalloutColumnCount(block);
  const applyColumns = (count) => {
    setColumnsOpen(false);
    if (count === columnCount) return;
    if (count <= 1) {
      const nextProps = { ...(block.props || {}) };
      delete nextProps.columns;
      const items = flattenCalloutLayout(block.children);
      onUpdate(block.id, {
        props: nextProps,
        children: items.length ? items : [createBlock('text')],
      });
      return;
    }
    onUpdate(block.id, { props: { ...block.props, columns: count } });
  };

  const handleCopy = async (e) => {
    e.stopPropagation();
    // Values, not {{tokens}} — callout Copy is for reading/sharing outside the editor.
    // Enter (new block) → blank line; Shift+Enter (<br>) → single newline.
    const text = blocksToPlainText(block.children || [], {
      variablesAsValues: true,
      resolveVariable,
      blockSeparator: '\n\n',
    });
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard unavailable — nothing sensible to do */
    }
  };

  return {
    copied,
    columnsOpen,
    setColumnsOpen,
    columnsRef,
    columnCount,
    applyColumns,
    handleCopy,
  };
};
